import React, { useCallback, useRef, useState } from "react";
import { getImageInfo, ImageInfo } from "../utils/image-utils";

export type ProcessResponse = {
  success: boolean;
  data: Array<{
    latitude: number;
    longitude: number;
    label?: string;
    category?: string;
  }>;
  categories: string[];
  bounds: { north: number; south: number; east: number; west: number };
};

interface UploadImageProps {
  /** API endpoint to POST the file to. Defaults to "/api/uploads/" */
  action?: string;

  /** Called with the processed payload returned by the server */
  onSuccess?: (payload: File) => void;
  onChange?: (payload: File) => void;
  /** Optional CSRF token. If omitted, we'll try reading the csrftoken cookie (Django default). */
  csrfToken?: string;
  /** Optional maximum file size in bytes (default 25MB) */
  maxSizeBytes?: number;
}

function getCookie(name: string) {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(";").shift() || null;
  return null;
}

const prettyBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"] as const;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const UploadImage: React.FC<UploadImageProps> = ({
  action = "/api/uploads/",
  onSuccess,
  onChange,
  csrfToken,
  maxSizeBytes = 25 * 1024 * 1024,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [imageInfo, setImageInfo] = useState<ImageInfo | null>(null);
  // const [isUploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  // const [uploadImage, { isLoading: isUploading }] = useUploadImageMutation();

  const selectFile = useCallback((f: File | null) => {
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    // Basic validation: extension and size
    const name = f.name.toLowerCase();
    const isImage = name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg") || f.type === "image/png" || f.type === "image/jpg" || f.type === "image/jpeg";
    if (!isImage) {
      setError("Please select a .png, .jpg, or .jpeg file.");
      setFile(null);
      return;
    }
    if (f.size > maxSizeBytes) {
      setError(`File too large. Max ${prettyBytes(maxSizeBytes)}.`);
      setFile(null);
      return;
    }
    setFile(f);
    // Don't auto-upload, let user control it
  }, [maxSizeBytes]);

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    if (f) {
      selectFile(f);
      onChange?.(f);
      getImageInfo(f).then((info: ImageInfo) => {
        setImageInfo(info);
      });
    }
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    selectFile(f);
  };

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const onDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  return (
    <div className="w-full bg-white rounded-lg border-none mt-4">
      <div
        className={`relative rounded-2xl border-2 border-dashed transition-colors ${
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
        } ${file ? 'p-0' : 'p-6'}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".png, .jpg, .jpeg"
          onChange={onInputChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label="Upload Image"
        />

        {file ? (
          // Image Preview
          <div
            className="relative w-full h-64 cursor-pointer"
            onClick={() => inputRef.current?.click()}
          >
            <img
              src={URL.createObjectURL(file)}
              alt="Preview"
              className="w-full h-full object-cover rounded-2xl pointer-events-none"
            />

            {/* File Details Overlay */}
            <div className="absolute top-3 left-3 right-3 bg-white/70 backdrop-blur-sm rounded-lg p-3 w-fit">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {file.name}
                  </p>
                  <div className="flex flex-col items-left justify-between">
                    <p className="text-xs text-gray-600">
                      {imageInfo?.width}
                      x
                      {imageInfo?.height}
                    </p>
                    <p className="text-xs text-gray-600">
                      {prettyBytes(file.size)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 rounded-2xl flex items-center justify-center">
              <div className="opacity-0 hover:opacity-100 transition-opacity duration-200 text-white text-center">
                <p className="text-sm font-medium">
                  Click to change image
                </p>
                <p className="text-xs">
                  or drag & drop a new one
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Upload Area
          <div className="pointer-events-none flex flex-col items-center justify-center text-center">
            <svg
              className="mb-3 h-10 w-10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeWidth="1.5"
                d="M12 16V4m0 12l-3-3m3 3l3-3M3 16v2a2 2 0 002 2h14a2 2 0 002-2v-2"
              />
            </svg>
            <p className="text-sm text-gray-700">
              <strong>
                Click to upload
              </strong>
              {' '}
              or drag & drop an image file
            </p>
            <p className="text-xs text-gray-500">
              Only .png, .jpg, and .jpeg files are supported
            </p>
          </div>
        )}
      </div>


      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

export default UploadImage;
